import { FunctionComponent, useEffect, useState } from 'react';
import styles from './EvaluationResult.module.scss';

// Redux State & Actions
import { getEvaluationReportThunk } from '@spike/reports-action';

// Models & Types
import { EvaluationReport } from '@spike/report-model';
import { dispatch } from 'store';

// Hooks
import { useApiClientWrapper } from 'hooks';

// UI Components
import { Spinner } from 'components/UI';
import { Label } from 'components/UI/V3/Forms/Label/Label';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faScissors, faBan, faHotel, faPen } from '@fortawesome/pro-regular-svg-icons';
import { faCircleCheck, faCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import Button from 'components/UI/V3/Button/Button';

export interface EvaluationResultProps {
    evaluationReportId: number;
    isCanceled: boolean;
    onEdit?: () => void;
    className?: string;
}

const FINAL_RESULTS_CONFIG: {
    key: keyof EvaluationReport;
    label: string;
    icon: any;
}[] = [
    { key: 'finalResultGrooming', label: 'Grooming', icon: faScissors },
    { key: 'finalResultDaycare', label: 'Daycare', icon: faBan },
    { key: 'finalResultBoarding', label: 'Boarding', icon: faHotel }
];

export const EvaluationResult: FunctionComponent<EvaluationResultProps> = ({
    evaluationReportId,
    onEdit,
    className,
    isCanceled
}) => {
    const apiClientWrapper = useApiClientWrapper();

    const [evaluationReport, setEvaluationReport] = useState<EvaluationReport | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        dispatch(getEvaluationReportThunk(apiClientWrapper, evaluationReportId))
            .then(setEvaluationReport)
            .finally(() => setLoading(false));
    }, [evaluationReportId]);

    if (loading || !evaluationReport) {
        return <Spinner />;
    }

    return (
        <div className={`${styles.container} ${className || ''}`}>
            <div className={styles.title}>
                <Label className={styles.sectionLabel}>Results</Label>
                {!isCanceled && onEdit && (
                    <Button
                        label="Edit"
                        leftIcon={faPen}
                        size="inline"
                        variant="text-green"
                        className={styles.editButton}
                        onClick={onEdit}
                    />
                )}
            </div>
            <div className={styles.results}>
                {FINAL_RESULTS_CONFIG.map(({ key, label, icon }) => {
                    const resultValue = evaluationReport[key];
                    const resultIcon =
                        resultValue === 'pass' ? faCircleCheck : resultValue === 'fail' ? faCircleXmark : faCircleXmark;

                    const resultClass =
                        resultValue === 'pass' ? styles.pass : resultValue === 'fail' ? styles.fail : styles.unknown;

                    return (
                        <div
                            key={key}
                            className={styles.resultItem}
                        >
                            <div className={styles.resultInfo}>
                                <FontAwesomeIcon
                                    icon={icon}
                                    size="sm"
                                    className={styles.icon}
                                />
                                <Label className={styles.resultLabel}>{label}</Label>
                            </div>
                            <FontAwesomeIcon
                                icon={resultIcon}
                                size="lg"
                                className={`${styles.resultIcon} ${resultClass}`}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
